// Compact, non-blocking AI notifications shown directly beneath the
// "Handled automatically" banner on the Submit page.
//
// Presentation only: nothing here changes readiness rules, validation,
// payment logic or persistence. Two grouped notices — one required, one
// optional — each with a single review action and a dismiss control.

import { useEffect, useState } from "react";
import { AlertTriangle, ArrowRight, CheckCircle2, Lightbulb, X } from "lucide-react";

export type NotificationFieldKey = "vendor" | "zip" | "vendorPhone" | "vendorAddress";

const LABELS: Record<NotificationFieldKey, string> = {
  vendor: "Towing / Storage Company",
  zip: "Service Location ZIP Code",
  vendorPhone: "Business Phone",
  vendorAddress: "Business Address",
};

/** Conversational explanation shown once per group. */
const REQUIRED_REASON: Record<"vendor" | "zip", string> = {
  vendor: "We couldn't confidently identify the towing/storage business.",
  zip: "We couldn't determine where the service occurred.",
};

export function AiNotifications({
  requiredFields,
  values,
  isFilled,
  onGoToField,
}: {
  /** Server-decided customer-enterable requirements. */
  requiredFields: string[];
  values: Record<NotificationFieldKey, string>;
  /** Same validity rule the form uses. */
  isFilled: (field: string) => boolean;
  onGoToField: (field: NotificationFieldKey, options: { pulse: boolean }) => void;
}) {
  /* Dismissal is view-state only — it never marks anything resolved, and a
     reload brings an unresolved notification back. */
  const [requiredDismissed, setRequiredDismissed] = useState(false);
  const [optionalDismissed, setOptionalDismissed] = useState(false);
  const [successDismissed, setSuccessDismissed] = useState(false);

  const requiredKeys = (["vendor", "zip"] as const).filter((k) => requiredFields.includes(k));
  const openRequired = requiredKeys.filter((k) => !isFilled(k));

  /* A recommendation only rides along with an unresolved required field:
     phone follows the business name, address follows the ZIP code. */
  const openOptional = (
    [
      ["vendorPhone", "vendor"],
      ["vendorAddress", "zip"],
    ] as const
  )
    .filter(
      ([opt, dep]) =>
        requiredFields.includes(dep) &&
        !isFilled(dep) &&
        values[opt].trim().length === 0,
    )
    .map(([opt]) => opt);

  const allRequiredResolved = requiredKeys.length > 0 && openRequired.length === 0;
  const showRequired = openRequired.length > 0 && !requiredDismissed;
  const showOptional = openOptional.length > 0 && !optionalDismissed;
  const showSuccess = allRequiredResolved && !successDismissed;

  /* The success notice is confirmation, not a task — it fades on its own. */
  useEffect(() => {
    if (!showSuccess) return;
    const t = window.setTimeout(() => setSuccessDismissed(true), 3500);
    return () => window.clearTimeout(t);
  }, [showSuccess]);

  if (!showRequired && !showOptional && !showSuccess) return null;

  return (
    <div data-testid="ai-notifications" className="space-y-2.5" aria-live="polite">
      {showSuccess ? (
        <div
          data-testid="ai-notification-success"
          role="status"
          className="flex items-center gap-2.5 rounded-xl border border-savings/40 bg-savings-soft/60 px-3.5 py-2 animate-fade-in"
        >
          <CheckCircle2 className="h-4 w-4 shrink-0 text-savings" aria-hidden />
          <p className="flex-1 text-sm font-semibold text-navy">You're ready for payment.</p>
          <DismissButton onClick={() => setSuccessDismissed(true)} />
        </div>
      ) : null}

      {showRequired ? (
        <div
          data-testid="ai-notification-required"
          role="status"
          className="flex items-start gap-3 rounded-xl border-2 border-destructive/45 bg-destructive/[0.035] px-4 py-3 shadow-card animate-fade-in"
        >
          <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-destructive" aria-hidden />
          <div className="min-w-0 flex-1">
            <p className="text-[15px] font-bold leading-snug text-destructive">
              Almost done — we just need {openRequired.length}{" "}
              {openRequired.length === 1 ? "detail" : "details"}
            </p>
            <ul className="mt-1 space-y-0.5">
              {openRequired.map((k) => (
                <li key={k} className="text-xs leading-snug text-muted-foreground">
                  <span className="font-semibold text-navy">{LABELS[k]}</span> —{" "}
                  {REQUIRED_REASON[k]}
                </li>
              ))}
            </ul>
            <ReviewButton
              testId="review-required-fields"
              label="Review Required Fields"
              tone="required"
              onClick={() => onGoToField(openRequired[0]!, { pulse: true })}
            />
          </div>
          <DismissButton onClick={() => setRequiredDismissed(true)} />
        </div>
      ) : null}

      {showOptional ? (
        <div
          data-testid="ai-notification-optional"
          role="status"
          className="flex items-start gap-2.5 rounded-xl border border-warning/25 bg-warning/[0.035] px-3.5 py-1.5 animate-fade-in"
        >
          <Lightbulb className="mt-0.5 h-3.5 w-3.5 shrink-0 text-warn-strong/80" aria-hidden />
          <div className="min-w-0 flex-1">
            <p className="text-xs font-semibold text-navy/85">Improve your review accuracy</p>
            <p className="mt-0.5 text-[11px] leading-snug text-muted-foreground">
              {openOptional.map((k) => LABELS[k]).join(", ")} — optional, but can improve
              verification.
            </p>
            <ReviewButton
              testId="review-optional-fields"
              label="Review Optional Fields"
              tone="optional"
              onClick={() => onGoToField(openOptional[0]!, { pulse: false })}
            />
          </div>
          <DismissButton onClick={() => setOptionalDismissed(true)} />
        </div>
      ) : null}
    </div>
  );
}


function ReviewButton({
  testId,
  label,
  tone,
  onClick,
}: {
  testId: string;
  label: string;
  tone: "required" | "optional";
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      data-testid={testId}
      onClick={onClick}
      className={`mt-1 inline-flex items-center gap-1 font-semibold text-trust underline-offset-2 transition-colors hover:text-trust/80 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-trust focus-visible:ring-offset-2 ${
        tone === "required" ? "text-xs" : "text-[11px]"
      }`}
    >
      {label}
      <ArrowRight className={tone === "required" ? "h-3.5 w-3.5" : "h-3 w-3"} aria-hidden />
    </button>
  );
}

function DismissButton({ onClick }: { onClick: () => void }) {
  return (
    <button
      type="button"
      aria-label="Dismiss notification"
      onClick={onClick}
      className="-mr-0.5 rounded-md p-1 text-muted-foreground transition-colors hover:bg-black/5 hover:text-navy focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-trust"
    >
      <X className="h-3.5 w-3.5" aria-hidden />
    </button>
  );
}
